import api from "../../../utils/api";
import type { FirestoreTimestamp, Lead } from "./leadsService";

// ============================================================================
// CRM Companies (Empresas) — frontend service. Companies are created
// automatically from leads server-side AND from web prospecting; the console
// reads/manages them here.
//   • list   (authed): GET    /crm/companiesList   (with live lead/contact counts)
//   • get    (authed): GET    /crm/companies?id=
//   • update (authed): PUT    /crm/companies?id=
//   • person (authed): POST   /crm/companies/person?id=   (adds a person → lead)
//   • delete (authed): DELETE /crm/companies?id=
// ============================================================================

export type CompanyStage = "prospect" | "customer" | "inactive";

/**
 * Raw contact data carried over from the web-prospecting source (Google Places)
 * when a business is staged as a company. Lets us call/prefill before anyone has
 * added a named contact person. All fields best-effort (the source is noisy).
 */
export interface CompanyProspect {
  phone?: string | null;
  whatsapp?: string | null;
  website?: string | null;
  address?: string | null;
  /** State / province, carried over individually from the prospect. */
  state?: string | null;
  /** Country, carried over individually from the prospect. */
  country?: string | null;
  rating?: number | null;
  location?: { lat: number; lng: number } | null;
  placeId?: string | null;
}

export interface Company {
  id: string;
  name: string;
  nameKey?: string;
  industry: string | null;
  size: string | null;
  city: string | null;
  stage: CompanyStage;
  source: string | null;
  product?: string | null;
  /** Logo de la empresa (HubSpot `hs_logo_url`); sin él se usa el icono de su sitio. */
  logoUrl?: string | null;
  domain?: string | null;
  website?: string | null;
  leadsCount: number;
  contactsCount: number;
  /** Present for companies created from web prospecting; holds the dialable phone. */
  prospect?: CompanyProspect | null;
  /** Compañía de la plataforma vinculada 1:1 (companies.crmCompanyId, #1069). */
  platformCompanyId?: string | null;
  createdAt?: FirestoreTimestamp | string | null;
  updatedAt?: FirestoreTimestamp | string | null;
}

/** The dialable number for a company (prospect phone/whatsapp), or null. */
export function companyPhone(c: Company): string | null {
  return c.prospect?.whatsapp || c.prospect?.phone || null;
}

/** "State, Country" label for a company (from its prospect data), or null. */
export function companyLocation(c: Company): string | null {
  const parts = [c.prospect?.state, c.prospect?.country].filter(Boolean);
  return parts.length ? parts.join(", ") : null;
}

/**
 * The company's website as a clickable href, or null. Prospect websites come
 * from Google Places and are usually full URLs, but we normalize defensively so
 * a bare "acme.com" still links correctly.
 */
export function companyWebsiteUrl(c: Company): string | null {
  const raw = c.prospect?.website?.trim();
  if (!raw) return null;
  return /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
}

/** Short, display-friendly label for a website URL (host without protocol). */
export function companyWebsiteLabel(c: Company): string | null {
  const raw = c.prospect?.website?.trim();
  if (!raw) return null;
  return raw.replace(/^https?:\/\//i, "").replace(/\/+$/, "");
}

export interface CompaniesPage {
  data: Company[];
  totalItems: number;
  totalPages: number;
  currentPage: number;
}

interface CompaniesListParams {
  page?: number;
  limit?: number;
  stage?: CompanyStage;
}

/**
 * A company without a name (HubSpot creates them from an email domain) is shown
 * by its domain. `name` is typed as a string and every list sorts and searches
 * by it: a null here blanked the whole Empresas page (`a.name.localeCompare`).
 */
export function withDisplayName<T extends { name?: string | null; domain?: string | null }>(
  company: T,
): Omit<T, "name"> & { name: string } {
  const name = (company.name ?? "").trim() || (company.domain ?? "").trim() || "Sin nombre";
  return { ...company, name };
}

export async function fetchCompanies(params: CompaniesListParams = {}): Promise<CompaniesPage> {
  const { page = 1, limit = 200, stage } = params;
  const res = await api.get("/crm/companiesList", {
    params: { page, limit, ...(stage ? { stage } : {}) },
  });
  const body = res.data ?? {};
  return {
    data: Array.isArray(body.data) ? body.data.map(withDisplayName) : [],
    totalItems: body.meta?.totalItems ?? 0,
    totalPages: body.meta?.totalPages ?? 1,
    currentPage: body.meta?.currentPage ?? page,
  };
}

/** A company plus its linked leads — returned by the single-company endpoint. */
export interface CompanyDetail extends Company {
  leads: Lead[];
}

/**
 * Fetch a single company with its full detail and the leads (contact people)
 * registered against it. Powers the empresa detail panel.
 */
export async function getCompany(id: string): Promise<CompanyDetail> {
  const res = await api.get(`/crm/companies`, { params: { id } });
  // The single-company endpoint wraps as response(res, req, 200, { message, data }),
  // so the actual company payload lives at res.data.data.data (one level deeper
  // than the paginated list, which uses pageResponse).
  const data = res.data?.data?.data ?? {};
  return { ...data, leads: Array.isArray(data.leads) ? data.leads : [] };
}

export async function updateCompany(
  id: string,
  patch: Partial<Pick<Company, "name" | "industry" | "size" | "city" | "stage" | "product">>,
): Promise<Company> {
  const res = await api.put(`/crm/companies`, patch, { params: { id } });
  return res.data?.data?.data;
}

export async function deleteCompany(id: string): Promise<void> {
  await api.delete(`/crm/companies`, { params: { id } });
}

/** Input for adding a named person to a company. */
export interface CompanyPersonInput {
  name: string;
  whatsapp?: string | null;
  email?: string | null;
  role?: string | null;
}

/**
 * Add a person to a company — the moment a prospected company becomes a real
 * LEAD. The created lead inherits the company's product and lands in the
 * Bandeja, the Pipeline board and the call lists. Returns the new lead.
 */
export async function addCompanyPerson(
  companyId: string,
  input: CompanyPersonInput,
): Promise<Lead> {
  const res = await api.post(`/crm/companies/person`, input, { params: { id: companyId } });
  return res.data?.data?.data;
}
